import apiService from '@/util/request.js';
import { extend } from 'lodash';

/**
 * module that contains some functions that deal with vehicles/equipments/services
 * @module vehicleService */

/**
 * get ficode split into it's parts
 * @param {String} ficode
 * @returns {Object}
 */
const decodeFicode = code => {
  return {
    fza: code.substring(0, 2),
    hst: code.substring(2, 5),
    ht: code.substring(5, 8),
    ut: code.substring(8, 11)
  };
};

/**
 * fetch dimension values by external ids
 * @param {String[]} params.externalIds
 * @returns {AxiosPromise<any>}
 */
function getDimensionValues(params) {
  extend(params, {
    calculable: true,
    offset: 0,
    limit: 1000
  });
  return apiService.get('smp/api/taxonomy/dimension_values', { params });
}

/**
 * fetch services for a vehicle
 * @param {String} params.fza
 * @param {String} params.hst
 * @param {String} params.ht
 * @param {String} params.ut
 * @returns {AxiosPromise<any>}
 */
function getServices(params) {
  return apiService.get('/smp/api/taxonomy/services', { params });
}

/**
 * fetch maintenance positions for a service
 * @param {Object} state vuex rootstate
 * @param {Object} service service object
 * @returns {AxiosPromise<any>}
 */
const getMaintenancePositions = (service, state) => {
  const params = {
    'fiCode': state.vehicleIdentification.vehicleType.fiCode,
    'constructionTime': state.vehicleIdentification.vehicleType.constructionTime.valueOf()
  };
  let avs = state.vehicleEquipments.selectedEquipments.map(e => {
    if (e.equipmentCode) return e.equipmentCode;
    if (e.externalId.includes('AVMO')) return e.externalId.split('-')[3];
    return false;
  }).filter(e => e);
  if (avs && avs.length) { extend(params, { avs }) }
  return apiService.get(`/smp/api/maintenances/intervals/${service.id}/positions`, { params })
};

/**
 * fetch vehicles by hsn-tsn
 * @param {String} formData.hsn hsn
 * @param {String} formData.tsn tsn
 * @returns {AxiosPromise<any>}
 */
const getVehiclesHsnTsn = formData => {
  return apiService.get('/smp/api/taxonomy/vehicles', { params: { 'hsnTsn': `${formData.hsn}-${formData.tsn}` } })
};

/**
 * fetch vehicle avs (some equipment object) by service
 * @param {String} params.fiCode ficode of a vehicle
 * @param {String} params.constructionTime construction time of a vehicle
 * @param {String[]} params.serviceIds service ids of selected services
 * @param {String} params.fuelIds external id of a fuel type
 * @param {String} params.avGroups type of avs
 * @returns {AxiosPromise<any>}
 */
const getAvsByService = params => {
  return apiService.get('/smp/api/taxonomy/services/avs/options', { params })
};

/**
 * fetch vehicle avs (some equipment object)
 * @param {String} params.fza
 * @param {String} params.hst
 * @param {String} params.ht
 * @param {String} params.ut
 * @param {String} params.constructionTime construction time of a vehicle
 * @param {String} params.fuelId external id of a fuel type
 * @param {String} params.avGroups type of avs
 * @param {String} params.collectAv
 * @returns {AxiosPromise<any>}
 */
const getAvs = (params) => {
  return apiService.get('/smp/api/taxonomy/services/avs', { params })
};

const getVehicleTypeDetails = (eCode) => {
  return apiService.get('/smp/api/taxonomy/vehicles', { params: { fiCode: eCode.substring(0, 11) } }).then((res) => res.data);
}

export default {
  getDimensionValues,
  decodeFicode,
  getServices,
  getMaintenancePositions,
  getVehiclesHsnTsn,
  getAvsByService,
  getAvs,
  getVehicleTypeDetails
}
